import { Box, Paper, styled } from '@mui/material';
import { DownloadIcon, PlusCircleIcon } from '@synergy/theme-next';
import { FormattedMessage } from 'react-intl';
import { Link, useSearchParams } from 'react-router-dom';
import { PropsWithChildren, ReactNode, useEffect } from 'react';
import { ReceiptAlert } from '@synergy/models';

import { Filter, FilterChips } from '../filter';
import { PoolButton } from '../button/pool-button';
import { PoolPageToolbar, PoolSubToolbar } from '../navigation';
import { PoolSpinner, PoolSpinnerProps } from '../spinner/pool-spinner';
import { PoolStickyHeader } from '../sticky-header/pool-sticky-header';

const Container = styled(Box)(() => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
}));

const NavLink = styled(Link)(() => ({
    textDecoration: 'none',

    '& button': {
        height: '100%',
    },
}));

const StyledSpinner = styled(PoolSpinner)(() => ({
    flex: 1,
}));

const StyledPaper = styled(Paper)(() => ({
    borderRadius: 0,

    '& div:first-child': {
        margin: 0,
        boxShadow: 'none',
    },
}));

interface OverviewProps extends PropsWithChildren, PoolSpinnerProps {
    newLabelMessage?: ReactNode;
    titleMessage?: ReactNode;
    pathname?: string;
    requesting?: boolean;
    filters?: ReactNode;
    alert?: ReceiptAlert;
    useLegacyFilter?: boolean;
    additionalItemsLeft?: ReactNode;
    additionalItemsRight?: ReactNode;
    additionalItemsBottom?: ReactNode;
    defaultSearchParams?: URLSearchParams;
    tabs?: ReactNode;
    onResetData?: () => void;
    onExport?: () => void;
    onAlertChange?: (alert: ReceiptAlert | null) => void;
}

export const Overview = (props: OverviewProps) => {
    const {
        requesting,
        newLabelMessage,
        pathname,
        additionalItemsLeft,
        additionalItemsRight,
        additionalItemsBottom,
        tabs,
        onExport,
        titleMessage,
        filters,
        alert,
        onAlertChange,
        children,
        useLegacyFilter,
        defaultSearchParams,
        onResetData,
        enabled: _spinnerEnabled,
        ...spinnerProps
    } = props;
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        onResetData?.();
        return () => {
            onResetData?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <StyledSpinner enabled={requesting} {...spinnerProps}>
            <Container>
                <Filter
                    searchParams={searchParams}
                    defaultSearchParams={defaultSearchParams}
                    onUpdateSearchParams={setSearchParams}
                    useLegacyFilter={useLegacyFilter}
                >
                    <PoolStickyHeader>
                        <PoolPageToolbar
                            mainToolbarLeftChildren={
                                <>
                                    {newLabelMessage && pathname && (
                                        <NavLink to={pathname + '/new'}>
                                            <PoolButton styleType="toolbar" startIcon={<PlusCircleIcon />}>
                                                {newLabelMessage}
                                            </PoolButton>
                                        </NavLink>
                                    )}
                                    {additionalItemsLeft}
                                </>
                            }
                            mainToolbarRightChildren={
                                <>
                                    {props.onExport && (
                                        <PoolButton styleType="toolbar" onClick={onExport} endIcon={<DownloadIcon />}>
                                            <FormattedMessage id="app.common.export" />
                                        </PoolButton>
                                    )}
                                    {additionalItemsRight}
                                </>
                            }
                        />
                    </PoolStickyHeader>
                    <StyledPaper>
                        <PoolSubToolbar titleMessage={titleMessage} alert={alert} onAlertChange={onAlertChange}>
                            {filters}
                        </PoolSubToolbar>
                        {tabs}
                        {additionalItemsBottom}
                    </StyledPaper>
                    <FilterChips />
                    {children}
                </Filter>
            </Container>
        </StyledSpinner>
    );
};
